/**
 * Table displaying tentative holds with challenge/confirm actions
 */

import React, { useState } from 'react';
import {
  Stack,
  Typography,
  Button,
  Box,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  Checkbox,
  Chip,
  Tooltip,
  Alert,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { MobileHoldCard } from './MobileHoldCard';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { HoldList } from 'api-client/models';
import { formatDateLong } from '@/utils/dateUtils';
import { STATUS_LABELS } from '../../../utils/constants';
import { getStatusColor, getPositionBadgeStyles, getPositionText } from '../../../utils/formatters';
import { isHoldActionable, isHoldDetailSelectable } from '../../../utils/holdSelection';

interface TentativeHoldsTableProps {
  holds: HoldList[];
  holdsLoading: boolean;
  selectedHoldIds: Set<string>;
  selectedHoldForDetails: HoldList | null;
  onSelectHold: (holdId: string) => void;
  onSelectAllHolds: () => void;
  onSelectHoldForDetails: (hold: HoldList) => Promise<void>;
  onConfirmChallenge: () => void;
  onReleaseHolds: () => Promise<void>;
  onRefresh: () => Promise<void>;
  onOpenChallengeResponse: (hold: HoldList) => void;
  getConfirmChallengeButtonText: () => string;
}

export const TentativeHoldsTable: React.FC<TentativeHoldsTableProps> = ({
  holds,
  holdsLoading,
  selectedHoldIds,
  selectedHoldForDetails,
  onSelectHold,
  onSelectAllHolds,
  onSelectHoldForDetails,
  onConfirmChallenge,
  onReleaseHolds,
  onRefresh,
  onOpenChallengeResponse,
  getConfirmChallengeButtonText,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const eligibleHolds = holds.filter(isHoldActionable);
  const allSelected = eligibleHolds.length > 0 && eligibleHolds.every((hold) => selectedHoldIds.has(hold.id));

  const handleRefresh = async (e: React.MouseEvent) => {
    e.preventDefault();
    const scrollPosition = window.scrollY;
    setRefreshError(null);
    try {
      await onRefresh();
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    } catch (err) {
      setRefreshError(err instanceof Error ? err.message : 'Failed to refresh dates. Reload to verify their current state.');
    }
  };

  return (
    <>
      {refreshError && <Alert severity="error" sx={{ mb: 2 }}>{refreshError}</Alert>}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2, ...(selectedHoldIds.size > 0 && { position: { xs: 'sticky', sm: 'static' }, top: 64, zIndex: 2, bgcolor: 'background.paper', p: 1, boxShadow: 1 }) }}
      >
        <Typography variant="body2" color="text.secondary">
          {holds.length} holds · Radio: date details. Checkbox: batch action.
        </Typography>
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleRefresh}
            disabled={holdsLoading}
            startIcon={holdsLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Refresh
          </Button>
          {selectedHoldIds.size > 0 && (
            <>
              <Button
                size="small"
                variant="contained"
                onClick={onConfirmChallenge}
                disabled={holdsLoading}
              >
                {getConfirmChallengeButtonText()}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => void onReleaseHolds()}
                disabled={holdsLoading}
              >
                Release Selected
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {isMobile ? (
        <Stack spacing={1} aria-label="Show dates" aria-busy={holdsLoading}>
          <FormControlLabel sx={{ m: 0 }} label="Select all eligible holds" control={
            <Checkbox checked={allSelected}
              indeterminate={!allSelected && eligibleHolds.some((hold) => selectedHoldIds.has(hold.id))}
              disabled={holdsLoading || eligibleHolds.length === 0} onChange={onSelectAllHolds} />
          } />
          {holdsLoading && <CircularProgress size={24} aria-label="Loading dates" />}
          {holds.length === 0 && !holdsLoading && <Typography>No hold dates yet.</Typography>}
          {holds.map((hold) => <MobileHoldCard key={hold.id} hold={hold}
            details={{ selected: selectedHoldForDetails?.id === hold.id,
              disabled: holdsLoading || !isHoldDetailSelectable(hold), onSelect: () => void onSelectHoldForDetails(hold) }}
            batch={{ selected: selectedHoldIds.has(hold.id),
              disabled: holdsLoading || !isHoldActionable(hold), onSelect: () => onSelectHold(hold.id) }}
            onRespond={holdsLoading ? undefined : onOpenChallengeResponse} />)}
        </Stack>
      ) : <TableContainer component={Paper} variant="outlined" role="region" tabIndex={0}
        aria-label="Show hold dates" aria-busy={holdsLoading} sx={{ position: 'relative' }}>
        {/* Loading overlay - shows loading state without replacing content */}
        {holdsLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <CircularProgress size={32} aria-label="Loading dates" />
          </Box>
        )}
        <Table size="small" aria-label="Tentative show holds" sx={{ minWidth: 1000 }}>
          <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Radio disabled />
                </TableCell>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    disabled={holdsLoading || eligibleHolds.length === 0}
                    onChange={onSelectAllHolds}
                    slotProps={{ input: { 'aria-label': 'Select all eligible holds' } }}
                  />
                </TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Promoter</TableCell>
                <TableCell>Challenge Info</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holds.length === 0 && !holdsLoading && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 3 }}>No hold dates yet.</TableCell>
                </TableRow>
              )}
              {holds.map((hold) => (
                <TableRow
                  key={hold.id}
                  hover
                  onClick={() => navigate(`/bookings/${hold.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      checked={selectedHoldForDetails?.id === hold.id}
                      disabled={holdsLoading || !isHoldDetailSelectable(hold)}
                      slotProps={{ input: { 'aria-label': `View details for ${hold.id}` } }}
                      onClick={(e) => {
                        e.stopPropagation();
                        void onSelectHoldForDetails(hold);
                      }}
                    />
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedHoldIds.has(hold.id)}
                      disabled={holdsLoading || !isHoldActionable(hold)}
                      slotProps={{ input: { 'aria-label': `Select hold ${hold.id}` } }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectHold(hold.id);
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatDateLong(hold.holdDate)}</TableCell>
                  <TableCell>{hold.stageName}</TableCell>
                  <TableCell>{hold.stageVenueName || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getPositionText(hold.holdPosition || 0)}
                      size="small"
                      sx={{
                        ...getPositionBadgeStyles(hold.holdPosition || 0),
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={hold.status === 'standby' ? STATUS_LABELS.standby : hold.status} color={getStatusColor(hold.status)} size="small" />
                    {hold.status === 'standby' && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        This date is confirmed. Standby holds cannot be confirmed or challenged.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{hold.promoterTeamName}</TableCell>
                  <TableCell>
                    {hold.status === 'challenged' && hold.challengeResponseDeadline ? (
                      // Being challenged - need to respond
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                Challenged by: {hold.challengerTeamName || 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                Challenged:{' '}
                                {hold.challengedAt
                                  ? new Date(hold.challengedAt).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                    })
                                  : 'Unknown'}
                              </Typography>
                              <Typography variant="body2">
                                Deadline:{' '}
                                {new Date(hold.challengeResponseDeadline).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </Typography>
                            </Box>
                          }
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <WarningIcon sx={{ color: 'statusText.inFlight', fontSize: 20 }} />
                            <Chip
                              label={`${(() => {
                                const deadline = new Date(hold.challengeResponseDeadline);
                                const now = new Date();
                                const timeRemaining = deadline.getTime() - now.getTime();
                                const hoursRemaining = timeRemaining > 0 ? Math.ceil(timeRemaining / (1000 * 60 * 60)) : 0;
                                return hoursRemaining;
                              })()}h left`}
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Stack>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenChallengeResponse(hold);
                          }}
                        >
                          Respond
                        </Button>
                      </Stack>
                    ) : hold.status === 'pending' && hold.hasInitiatedChallenge ? (
                      // You initiated a challenge - waiting for response
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title="Challenge initiated on higher position hold(s)">
                          <Chip
                            icon={<WarningIcon />}
                            label="Challenge Out"
                            size="small"
                            color="info"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
    </>
  );
};
